import { ArrowRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ReactElement, ReactNode } from "react";

interface PropPeekProps {
  icon: LucideIcon;
  title: string;
  /** Right-aligned summary in the header (e.g. "5 of 14"). */
  meta?: ReactNode;
  children: ReactNode;
  /** Footer deep-link label, e.g. "Open full notebook". */
  openLabel: string;
  onOpenFull: () => void;
}

/**
 * Shared shell for a world "door": a glanceable peek that lives in a prop
 * window and hands off to the feature's full page. Header + scrollable body +
 * one "open full →" action — the world never re-implements the page, it points
 * at it. (Notebook, Integrations, Inbox… all render through this.)
 */
export function PropPeek({
  icon: Icon,
  title,
  meta,
  children,
  openLabel,
  onOpenFull,
}: PropPeekProps): ReactElement {
  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-black/5 px-1 pb-2.5 dark:border-white/10">
        <Icon className="h-[18px] w-[18px] text-brand-coral-dark dark:text-brand-coral" strokeWidth={2} />
        <span className="text-[15px] font-bold text-black dark:text-white">{title}</span>
        {meta && (
          <span className="ml-auto text-[12px] font-medium text-black/40 dark:text-white/40">
            {meta}
          </span>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto py-1.5">{children}</div>

      <button
        onClick={onOpenFull}
        className="mt-1.5 flex shrink-0 items-center justify-center gap-1.5 rounded-xl border border-black/10 py-2.5 text-[13px] font-bold text-black/70 transition-colors hover:border-brand-coral/50 hover:text-brand-coral-dark dark:border-white/15 dark:text-white/70 dark:hover:border-brand-coral/60 dark:hover:text-brand-coral"
      >
        {openLabel}
        <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
      </button>
    </div>
  );
}
